import { Header1 } from 'structure/home/components/Typography';
import { useUserTotalAssets } from 'structure/pv2/vault/hooks/useUserTotalAssets';
import { useConnection } from 'wagmi';
import React, { useMemo, useState } from 'react';
import { useVaultListWithFallback } from 'structure/pv2/dynavault/hooks/useVaultList';
import { ModalVaultDeposit } from 'structure/pv2/vault/containers/ModalVaultDeposit';
import { Loader } from '@dapp/multi-lib/ui/loader';
import { DynaVault } from 'api/DynaVaultsAPI';
import { useAppDispatch } from 'store/hooks';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { showWalletConnectModal } from 'store/features/wallet/walletSlice';
import { Body1 } from 'components/Typography';
import {
  erc20TokensMap,
  migrationTokens,
  nativeTokens,
  stakingTokens,
  vaultTokens,
} from 'structure/capital-manager/tokens';
import {
  formatUSDQuoteAmount,
  getTokenBalance,
  getTokenQuoteAmount,
} from 'structure/capital-manager/utils/tokens';
import { DesktopContent } from 'structure/capital-manager/components/DesktopContent';
import { MobileContent } from 'structure/capital-manager/components/MobileContent';
import {
  TokenAction,
  TokenRecordType,
  TokenTableRow,
} from 'structure/capital-manager/types';
import { useNativeBalances } from 'structure/capital-manager/hooks/useNativeBalances';
import { formatUnits } from 'utils/units';
import {
  getErc20TokenBalance,
  useErc20TokenBalances,
} from 'utils/hooks/useErc20TokenBalances';
import { getTokenPrice, useTokenPrices } from 'utils/hooks/useTokenPrices';
import { useAllUserStakingPositions } from 'structure/staking/hooks/useStakingAPI';
import {
  useVaultListOnChainData,
  VaultOnChainData,
} from 'structure/pv2/dynavault/hooks/useVaultListBlockchain';
import { useClientRenderDelay } from 'utils/hooks/useClientRenderDelay';

export const CapitalManagerPage = () => {
  const connection = useConnection();
  const dispatch = useAppDispatch();

  const tokenPricesQuery = useTokenPrices();

  const stakingPositionsQuery = useAllUserStakingPositions({
    walletAddress: connection.address as `0x${string}`,
  });

  const vaultListQuery = useVaultListWithFallback();

  const vaultsOnChainDataQuery = useVaultListOnChainData({
    vaultAddressList: vaultListQuery.data
      ? vaultListQuery.data.map((vault) => vault.address)
      : [],
    walletAddress: connection.address as `0x${string}`,
    chainId: 8453,
    query: {
      staleTime: 15000,
      refetchInterval: 15000,
      enabled: !!vaultListQuery.isFetched && !!vaultListQuery.data,
    },
  });

  const tokens = useMemo(() => {
    return [
      ...vaultTokens.map((token) => ({
        address: token.address,
        chainId: token.chainId,
      })),
      ...stakingTokens.map((token) => ({
        address: token.address,
        chainId: token.chainId,
      })),
      ...migrationTokens.map((token) => ({
        address: token.address,
        chainId: token.chainId,
      })),
    ];
  }, []);

  const tokenBalancesQuery = useErc20TokenBalances({
    userAddress: connection.address as `0x${string}`,
    tokens: tokens,
  });

  const userTotalAssetsQuery = useUserTotalAssets({
    userAddress: connection.address as `0x${string}`,
    tokens,
  });

  const nativeTokenBalanceQuery = useNativeBalances({
    address: connection.address,
    query: { enabled: !!connection.address },
  });

  const [zapParams, setZapParams] = useState<{
    depositTokenAddress: `0x${string}`;
    vault: DynaVault;
  } | null>(null);

  const handleClickZap = (params: {
    depositTokenAddress: `0x${string}`;
    vault: DynaVault;
  }) => {
    setZapParams(params);
  };

  const handleCloseDepositModal = (open: boolean) => {
    if (!open) {
      setZapParams(null);
      userTotalAssetsQuery.refetch();
      vaultListQuery.refetch();
    }
  };

  const isLoading =
    tokenPricesQuery.isLoading ||
    nativeTokenBalanceQuery.isLoading ||
    userTotalAssetsQuery.isLoading ||
    vaultListQuery.isLoading;

  const tokenRowList: TokenTableRow[] = useMemo(() => {
    const stakingPositionList: TokenTableRow[] = (
      stakingPositionsQuery.data ?? []
    )
      .filter((position) => BigInt(position.userInfo.totalStaked) > BigInt(0))
      .map((position) => {
        const { pool, userInfo } = position;
        const balance =
          BigInt(userInfo.totalStaked) + BigInt(userInfo.claimable);

        const tokenConfig = stakingTokens.find(
          (t) =>
            t.address.toLowerCase() ===
              pool.depositTokenAddress.toLowerCase() &&
            t.chainId === (pool.chainId as number)
        );

        const price = getTokenPrice(
          tokenPricesQuery.data,
          tokenConfig?.coingeckoId
        );
        const balanceUsd =
          Number(formatUnits(balance, pool.depositTokenDecimals)) * price;

        return {
          recordType: TokenRecordType.LockedToken,
          tokenAction: TokenAction.None,
          tokenGroup: tokenConfig?.tokenGroup ?? 'altcoin',
          riskLevel: 'VERY_LOW',
          id: `${pool.chainId}-position-staking-${pool.stakingContractAddress}`,
          balance,
          address: pool.depositTokenAddress,
          chainId: pool.chainId as number,
          symbol: pool.depositTokenSymbol,
          name: pool.depositTokenName,
          decimals: pool.depositTokenDecimals,
          balanceUsd,
        };
      });

    const vaultPositionList: TokenTableRow[] = Object.values(
      (vaultsOnChainDataQuery.data ?? {}) as Record<string, VaultOnChainData>
    )
      .filter((vault) => vault.walletBalance > BigInt(0))
      .map((vault) => {
        const tokenConfig = vaultTokens.find(
          (t) => t.address.toLowerCase() === vault.referenceAsset?.toLowerCase()
        );

        let riskLevel =
          vaultListQuery.data?.find((v) => v.address === vault.address)
            ?.riskLevel || 'MEDIUM';

        if (riskLevel === 'USER_DEFINED') {
          riskLevel = 'HIGH';
        }

        return {
          recordType: TokenRecordType.LockedToken,
          tokenAction: TokenAction.None,
          tokenGroup: tokenConfig?.tokenGroup ?? 'stable',
          riskLevel: riskLevel,
          id: `${vault.chainId}-position-vault-${vault.address}`,
          balance: vault.walletBalance,
          address: vault.address,
          chainId: vault.chainId,
          symbol: vault.symbol,
          name: vault.name,
          decimals: vault.decimals,
          balanceUsd: vault.positionInUsd,
          isDeposit: false,
          isStable: tokenConfig?.isStable ?? false,
        };
      });

    const vaultTokenList: TokenTableRow[] = vaultTokens.map((token) => {
      const balance = getTokenBalance({
        assetsData: userTotalAssetsQuery.data,
        tokenAddress: token.address,
        chainId: token.chainId,
      });

      return {
        recordType: TokenRecordType.IdleToken,
        tokenAction: TokenAction.Zap,
        tokenGroup: token.tokenGroup,
        riskLevel: 'NONE',
        id: `${token.chainId}-${token.address}`,
        balance: balance,
        address: token.address,
        isDeposit: token.isDeposit,
        isStable: token.isStable,
        chainId: token.chainId,
        symbol: token.symbol,
        name: token.name,
        decimals: token.decimals,
        balanceUsd: Number(
          formatUSDQuoteAmount(
            getTokenQuoteAmount(
              userTotalAssetsQuery.data!,
              token.address,
              token.chainId
            )
          )
        ),
      };
    });

    const stakingTokenList: TokenTableRow[] = stakingTokens.map((token) => {
      const balance = getErc20TokenBalance(
        tokenBalancesQuery.data,
        token.address,
        token.chainId
      );

      const price = getTokenPrice(tokenPricesQuery.data, token.coingeckoId);

      const balanceUsd = Number(formatUnits(balance, token.decimals)) * price;

      return {
        recordType: TokenRecordType.IdleToken,
        tokenAction: TokenAction.Stake,
        tokenGroup: token.tokenGroup,
        riskLevel: 'NONE',
        id: `${token.chainId}-${token.address}`,
        balance: balance,
        address: token.address,
        chainId: token.chainId,
        symbol: token.symbol,
        name: token.name,
        decimals: token.decimals,
        balanceUsd: balanceUsd,
      };
    });

    const migrationTokenList: TokenTableRow[] = migrationTokens.map((token) => {
      const balance = getErc20TokenBalance(
        tokenBalancesQuery.data,
        token.address,
        token.chainId
      );

      const price = getTokenPrice(tokenPricesQuery.data, token.coingeckoId);
      const balanceUsd = Number(formatUnits(balance, token.decimals)) * price;

      return {
        recordType: TokenRecordType.IdleToken,
        tokenAction: TokenAction.Migrate,
        tokenGroup: token.tokenGroup,
        riskLevel: 'NONE',
        id: `${token.chainId}-${token.address}`,
        balance: balance,
        address: token.address,
        chainId: token.chainId,
        symbol: token.symbol,
        name: token.name,
        decimals: token.decimals,
        balanceUsd: balanceUsd,
      };
    });

    const nativeTokenList: TokenTableRow[] = nativeTokens.map((token) => {
      const balance =
        nativeTokenBalanceQuery.data?.[token.chainId] || BigInt(0);

      const price = getTokenPrice(tokenPricesQuery.data, token.coingeckoId);
      const balanceUsd = Number(formatUnits(balance, token.decimals)) * price;

      return {
        recordType: TokenRecordType.IdleToken,
        tokenAction: TokenAction.BuySFI,
        tokenGroup: token.tokenGroup,
        riskLevel: 'NONE',
        id: `${token.chainId}-native-${token.symbol}`,
        chainId: token.chainId,
        symbol: token.symbol,
        name: token.name,
        decimals: token.decimals,
        balance: balance,
        balanceUsd: balanceUsd,
        buySFITokenHref: token.buySFITokenHref,
      };
    });

    return [
      ...stakingPositionList,
      ...vaultPositionList,
      ...vaultTokenList,
      ...nativeTokenList,
      ...stakingTokenList,
      ...migrationTokenList,
    ]
      .filter((token) => {
        if (token.tokenAction === TokenAction.BuySFI) {
          return token.balanceUsd > 0.1;
        }
        return token.balanceUsd > 0.01;
      })
      .sort((a, b) => {
        return Number(b.balanceUsd) - Number(a.balanceUsd) > 0 ? 1 : -1;
      });
  }, [
    stakingPositionsQuery.data,
    vaultsOnChainDataQuery.data,
    tokenPricesQuery.data,
    userTotalAssetsQuery.data,
    tokenBalancesQuery.data,
    nativeTokenBalanceQuery.data,
  ]);

  const csr = useClientRenderDelay();

  if (!csr) {
    return null;
  }

  if (!connection.address) {
    return (
      <div className='px-4 xl:px-8'>
        <Header1 as='h1' className='text-center lg:text-left'>
          Capital Manager
        </Header1>
        <div className='my-20 flex min-h-[50vh] flex-col items-center gap-5'>
          <Body1 className='text-center text-white-100'>
            Connect your wallet to view your capital
          </Body1>
          <Button
            className='w-40'
            onClick={() => dispatch(showWalletConnectModal(true))}
          >
            Connect wallet
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='px-4 xl:px-8'>
      <Header1 as='h1' className='text-center lg:text-left'>
        Capital Manager
      </Header1>

      <div className='mt-8'>
        {isLoading && <Loader color='white' />}
        {!isLoading && userTotalAssetsQuery.data && (
          <>
            <div className='hidden lg:block'>
              <DesktopContent
                tokens={tokenRowList}
                vaultList={vaultListQuery.data ?? []}
                handleClickZap={handleClickZap}
              />
            </div>
            <div className='block lg:hidden'>
              <MobileContent
                tokens={tokenRowList}
                vaultList={vaultListQuery.data ?? []}
                handleClickZap={handleClickZap}
              />
            </div>
          </>
        )}
        {zapParams && (
          <ModalVaultDeposit
            open={!!zapParams}
            expandDetails={false}
            readOnlyInput={false}
            depositTokenAddress={zapParams.depositTokenAddress}
            depositTokenList={vaultTokens}
            depositTokenAmount={formatUnits(
              getTokenBalance({
                assetsData: userTotalAssetsQuery.data,
                tokenAddress: zapParams.depositTokenAddress,
                chainId: zapParams.vault.chainId,
              }),
              erc20TokensMap[zapParams.depositTokenAddress]?.decimals
            )}
            vaultIdentity={{
              address: zapParams.vault.address,
              chainId: zapParams.vault.chainId,
            }}
            onOpenChange={handleCloseDepositModal}
          />
        )}
      </div>
    </div>
  );
};
